import { Navigate } from "react-router-dom";
import { countActiveDeals } from "@app/types";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { useAssessmentRequests } from "@/hooks/useAssessmentRequests";

function Spinner() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  );
}

export function RootRedirect() {
  const { isAuthenticated, isLoading } = useAuth();
  const { currentTenant, isLoading: isTenantLoading } = useTenant();
  const {
    requests,
    isLoading: isRequestsLoading,
    error: requestsError,
  } = useAssessmentRequests();

  if (isLoading) {
    return <Spinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/sign-in" replace />;
  }

  if (isTenantLoading) {
    return <Spinner />;
  }

  if (!currentTenant) {
    return <Navigate to="/company-setup" replace />;
  }

  if (currentTenant.tenantType === "investor") {
    // Pipeline-first landing: an investor with any in-flight deal lands on the
    // Pipeline, otherwise the Portfolio (sv-0f2). Wait for the requests query so
    // we redirect once, correctly — never portfolio-then-pipeline flicker.
    if (isRequestsLoading) {
      return <Spinner />;
    }
    // On a failed requests fetch, `requests` falls back to `[]` — which is
    // indistinguishable from "no deals". Don't silently route to Portfolio and
    // hide active deals; land on the Pipeline (its own error banner then explains
    // the fetch failure) rather than choosing the wrong space from absent data.
    if (requestsError) {
      return <Navigate to="/pipeline" replace />;
    }
    return (
      <Navigate
        to={countActiveDeals(requests) > 0 ? "/pipeline" : "/portfolio"}
        replace
      />
    );
  }

  return <Navigate to="/assessments" replace />;
}
